import {
    BINARY_THREAD_SCORER_IDS,
    isContinuousScorerPassing,
    isPerfectOnlyScorerId,
    LOWER_IS_BETTER_SCORER_IDS,
    LOWER_IS_BETTER_SCORER_PASS_THRESHOLD,
} from '@fruitful/llm-service-shared';
import type { ClientScoreRowData } from '@mastra/client-js';

import { formatToSimpleDateTimeString } from '../../common/utils/formatting';

const getScoreCreatedAtMs = (score: ClientScoreRowData): number => {
    if (score.createdAt === '') {
        return 0;
    }

    return new Date(score.createdAt).getTime();
};

const getScoreScorerName = (score: ClientScoreRowData): string => {
    const { name } = score.scorer;
    return typeof name === 'string' ? name : score.scorerId;
};

export const isBinaryScorerId = (scorerId: string): boolean =>
    (BINARY_THREAD_SCORER_IDS as readonly string[]).includes(scorerId);

const isLowerIsBetterScorerId = (scorerId: string): boolean =>
    (LOWER_IS_BETTER_SCORER_IDS as readonly string[]).includes(scorerId);

const formatScoreValue = (score: number): string => {
    return `${Math.round(score * 100)}%`;
};

const formatBinaryScorerValue = (score: number): string => {
    return score >= 1 ? 'Pass' : 'Fail';
};

export const formatScorerValue = ({
    score,
    scorerId,
}: {
    score: number;
    scorerId: string;
}): string => {
    return isBinaryScorerId(scorerId)
        ? formatBinaryScorerValue(score)
        : formatScoreValue(score);
};

const getBinaryScorerColor = (score: number): string => {
    return score >= 1 ? '$green900' : '$red900';
};

const getContinuousScorerColor = (score: number): string => {
    if (isContinuousScorerPassing(score)) {
        return '$green900';
    }

    if (score >= 0.5) {
        return '$orange900';
    }

    return '$red900';
};

/** Lower scores are better (e.g. toxicity). */
const getLowerIsBetterScorerColor = (score: number): string => {
    if (score <= LOWER_IS_BETTER_SCORER_PASS_THRESHOLD) {
        return '$green900';
    }

    if (score <= 0.5) {
        return '$orange900';
    }

    return '$red900';
};

export const getScorerValueColor = ({
    score,
    scorerId,
}: {
    score: number;
    scorerId: string;
}): string => {
    if (isBinaryScorerId(scorerId)) {
        return getBinaryScorerColor(score);
    }

    if (isLowerIsBetterScorerId(scorerId)) {
        return getLowerIsBetterScorerColor(score);
    }

    // Perfect-only scorers (e.g. the deterministic checks bundle) are green at
    // 100% and red for anything less.
    if (isPerfectOnlyScorerId(scorerId)) {
        return score >= 1 ? '$green900' : '$red900';
    }

    return getContinuousScorerColor(score);
};

export const formatScoreCreatedAt = (score: ClientScoreRowData): string =>
    formatToSimpleDateTimeString(score.createdAt || null) || '—';

/**
 * Latest score per currently registered scorer for the agent. Omits unregistered
 * scorers and duplicate scorer ids (keeps newest by createdAt).
 */
export const scoresFromLatestRun = (
    scores: ClientScoreRowData[],
    registeredScorerIds: readonly string[],
): ClientScoreRowData[] => {
    if (scores.length === 0 || registeredScorerIds.length === 0) {
        return [];
    }

    const registeredScorerIdSet = new Set(registeredScorerIds);
    const latestByScorerId = new Map<string, ClientScoreRowData>();

    for (const score of scores) {
        if (!registeredScorerIdSet.has(score.scorerId)) {
            continue;
        }

        const existing = latestByScorerId.get(score.scorerId);
        if (
            existing == null ||
            getScoreCreatedAtMs(score) > getScoreCreatedAtMs(existing)
        ) {
            latestByScorerId.set(score.scorerId, score);
        }
    }

    return registeredScorerIds
        .map((scorerId) => latestByScorerId.get(scorerId))
        .filter((score): score is ClientScoreRowData => score != null)
        .sort((left, right) =>
            getScoreScorerName(left).localeCompare(getScoreScorerName(right)),
        );
};

/**
 * Registered scorer rows that are not the latest for that scorer, newest first
 * within each scorer name.
 */
export const scoresFromEarlierRuns = (
    scores: ClientScoreRowData[],
    latestScores: readonly ClientScoreRowData[],
    registeredScorerIds: readonly string[],
): ClientScoreRowData[] => {
    if (scores.length === 0 || registeredScorerIds.length === 0) {
        return [];
    }

    const registeredScorerIdSet = new Set(registeredScorerIds);
    const latestScoreIds = new Set(latestScores.map((score) => score.id));

    return scores
        .filter(
            (score) =>
                registeredScorerIdSet.has(score.scorerId) &&
                !latestScoreIds.has(score.id),
        )
        .sort((left, right) => {
            const nameCompare = getScoreScorerName(left).localeCompare(
                getScoreScorerName(right),
            );
            if (nameCompare !== 0) {
                return nameCompare;
            }

            return getScoreCreatedAtMs(right) - getScoreCreatedAtMs(left);
        });
};
